import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronUp, Send, Flame, Trophy, Clock, X,
  CheckCircle, XCircle, Loader2, Zap, Palette, ArrowRight
} from "lucide-react";

const C = {
  bg: "#0d0c0b", bg2: "#111010", card: "#161514", text: "#F5F2EB", text2: "#8a857d",
  gold: "#C9A055", red: "#C0392B", bSub: "rgba(255,255,255,0.06)", green: "#4ade80",
};
const F = { sans: "'Oswald', sans-serif", cond: "'Oswald', sans-serif", hand: "'Caveat', cursive", brush: "'Permanent Marker', cursive" };

const CATEGORIES = ["Animals", "People & Portraits", "Abstract", "Landscapes & Nature", "Objects & Still Life", "Pop Culture", "Other"];

const STATUS_CONFIG = {
  pending: { label: "PENDING", color: C.text2, bg: "rgba(255,255,255,0.05)" },
  accepted_commission: { label: "ACCEPTED", color: C.green, bg: "rgba(74,222,128,0.1)" },
  accepted_own: { label: "ACCEPTED", color: C.green, bg: "rgba(74,222,128,0.1)" },
  denied: { label: "DENIED", color: C.red, bg: "rgba(192,57,43,0.1)" },
  in_queue: { label: "IN THE QUEUE", color: C.gold, bg: `${C.gold}15` },
  completed: { label: "COMPLETED", color: C.green, bg: "rgba(74,222,128,0.15)" },
};

function getVoterId() {
  let id = localStorage.getItem("stc_voter_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("stc_voter_id", id); }
  return id;
}

export default function ChallengeBoard() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Animals");
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("votes");
  const [votedIds, setVotedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("stc_voted_ids") || "[]")); }
    catch { return new Set(); }
  });

  const load = useCallback(() => {
    http.get("/marker-collection/public/suggestions").then(({ data }) => {
      setSuggestions(data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error("Write your suggestion first"); return; }
    setSubmitting(true);
    try {
      await http.post("/marker-collection/public/suggestions", { text, category, name: name || "Anonymous" });
      toast.success("Suggestion submitted!");
      setText(""); setName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const vote = async (id) => {
    if (votedIds.has(id)) { toast.error("You already voted on this"); return; }
    try {
      await http.post(`/marker-collection/public/suggestions/${id}/vote`, { voter_id: getVoterId() });
      const newVoted = new Set(votedIds); newVoted.add(id);
      setVotedIds(newVoted);
      localStorage.setItem("stc_voted_ids", JSON.stringify([...newVoted]));
      setSuggestions(prev => prev.map(s => s.id === id ? { ...s, votes: (s.votes || 0) + 1 } : s));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    }
  };

  const accepted = useMemo(() => suggestions.filter(s => ["accepted_commission", "accepted_own", "in_queue", "completed"].includes(s.status)), [suggestions]);
  const pending = useMemo(() => suggestions.filter(s => s.status === "pending"), [suggestions]);
  const denied = useMemo(() => suggestions.filter(s => s.status === "denied"), [suggestions]);

  const displayed = useMemo(() => {
    let list = filter === "all" ? suggestions
      : filter === "accepted" ? accepted
      : filter === "pending" ? pending
      : filter === "denied" ? denied
      : suggestions.filter(s => s.category === filter);
    if (sort === "votes") list = [...list].sort((a, b) => (b.votes || 0) - (a.votes || 0));
    else if (sort === "newest") list = [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sort === "oldest") list = [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return list;
  }, [suggestions, filter, sort, accepted, pending, denied]);

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, overflowX: "hidden", minHeight: "100vh" }}>
        <style>{`
          .cb-grit { position: relative; }
          .cb-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          @keyframes cb-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          .cb-vote-btn:hover { transform: scale(1.1); }
          .cb-vote-btn:active { transform: scale(0.95); }
          .cb-card { transition: all 0.3s ease; }
          .cb-card:hover { transform: translateY(-2px); }
        `}</style>

        {/* Hero */}
        <section className="cb-grit relative py-10 sm:py-16" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <Link to="/marker-collection" className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase mb-6 hover:gap-3 transition-all"
              style={{ fontFamily: F.sans, color: C.text2 }} data-testid="cb-back-link">
              <ArrowLeft size={14} /> Back to Collection
            </Link>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Flame size={24} style={{ color: C.red }} />
                  <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.red }}>
                    COMMUNITY CHALLENGE
                  </p>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.9]"
                  style={{ fontFamily: F.cond, color: C.text }} data-testid="cb-title">
                  WHAT SHOULD<br/>I DRAW NEXT?
                </h1>
                <p className="mt-3 text-sm sm:text-base max-w-lg" style={{ fontFamily: F.sans, color: C.text2 }}>
                  Drop your wildest ideas below. Vote for the ones you want to see. I'll accept the challenge — or deny it. No hard feelings.
                </p>
              </div>

              {/* Stats */}
              <div className="flex gap-4 sm:gap-6">
                {[
                  { n: suggestions.length, l: "Ideas", icon: Zap, color: C.gold },
                  { n: accepted.length, l: "Accepted", icon: Trophy, color: C.green },
                  { n: pending.length, l: "Pending", icon: Clock, color: C.text2 },
                ].map(({ n, l, icon: Icon, color }) => (
                  <div key={l} className="text-center">
                    <Icon size={16} style={{ color }} className="mx-auto mb-1" />
                    <div className="text-2xl sm:text-3xl font-black" style={{ fontFamily: F.cond, color }}>{n}</div>
                    <div className="text-[8px] sm:text-[9px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Submit Form */}
        <section className="cb-grit relative" style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 relative z-10">
            <p className="text-[10px] tracking-[0.25em] uppercase font-bold mb-4" style={{ fontFamily: F.sans, color: C.gold }}>
              DROP YOUR IDEA
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="cb-submit-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <input value={text} onChange={e => setText(e.target.value)} maxLength={300}
                  placeholder="Draw a _____ (be specific, be weird, be bold)"
                  className="flex-1 px-4 py-3.5 text-sm outline-none placeholder:text-[#4a4740]"
                  style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.bSub}`, color: C.text }}
                  data-testid="cb-input-text" />
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className="px-4 py-3.5 text-[11px] tracking-[0.1em] uppercase outline-none cursor-pointer"
                  style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.bSub}`, color: C.text2, appearance: "auto" }}
                  data-testid="cb-input-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <input value={name} onChange={e => setName(e.target.value)} maxLength={50}
                  placeholder="Your name (optional)"
                  className="flex-1 px-4 py-3 text-sm outline-none placeholder:text-[#4a4740]"
                  style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.bSub}`, color: C.text }}
                  data-testid="cb-input-name" />
                <button type="submit" disabled={submitting || !text.trim()}
                  className="inline-flex items-center gap-3 px-8 py-3 text-[10px] tracking-[0.25em] uppercase font-bold transition-all disabled:opacity-40"
                  style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}
                  data-testid="cb-submit-btn">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  SUBMIT CHALLENGE
                </button>
              </div>
              <p className="text-[9px] tracking-wider" style={{ fontFamily: F.sans, color: C.text2 }}>
                {text.length}/300 characters
              </p>
            </form>
          </div>
        </section>

        {/* Accepted Challenges */}
        {accepted.length > 0 && (
          <section className="cb-grit relative" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <Trophy size={18} style={{ color: C.green }} />
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.green }}>
                  ACCEPTED CHALLENGES ({accepted.length})
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cb-accepted-grid">
                {accepted.map(s => {
                  const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.pending;
                  return (
                    <div key={s.id} className="cb-card px-4 py-3" style={{ border: `1px solid rgba(74,222,128,0.15)`, background: "rgba(74,222,128,0.03)" }}
                      data-testid={`cb-accepted-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-snug" style={{ fontFamily: F.sans, color: C.text }}>{s.text}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-[8px] tracking-[0.15em] uppercase px-2 py-0.5" style={{ fontFamily: F.sans, background: cfg.bg, color: cfg.color }}>
                              {cfg.label}
                            </span>
                            <span className="text-[8px] tracking-wider uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>{s.category}</span>
                            {s.status === "accepted_commission" && (
                              <span className="text-[8px] tracking-wider uppercase" style={{ fontFamily: F.sans, color: C.gold }}>by {s.submitter_name}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                          <ChevronUp size={12} style={{ color: C.green }} />
                          <span className="text-sm font-black" style={{ fontFamily: F.cond, color: C.green }}>{s.votes || 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Filter + Sort + Suggestion Log */}
        <section className="cb-grit relative" style={{ background: C.bg2 }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 relative z-10">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-6" data-testid="cb-controls">
              <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 no-scrollbar w-full sm:w-auto"
                style={{ WebkitOverflowScrolling: "touch" }}>
                {[
                  { key: "all", label: "ALL" },
                  { key: "pending", label: "PENDING" },
                  { key: "accepted", label: "ACCEPTED" },
                  { key: "denied", label: "DENIED" },
                  ...CATEGORIES.map(c => ({ key: c, label: c.toUpperCase() })),
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setFilter(key)}
                    className="px-3 py-1.5 text-[8px] sm:text-[9px] tracking-[0.15em] uppercase font-bold transition-all flex-shrink-0"
                    style={{
                      fontFamily: F.sans, borderRadius: "999px",
                      border: `1.5px solid ${filter === key ? C.gold : C.bSub}`,
                      background: filter === key ? C.gold : "transparent",
                      color: filter === key ? C.bg : C.text2,
                    }}
                    data-testid={`cb-filter-${key}`}>
                    {label}
                  </button>
                ))}
              </div>
              <select value={sort} onChange={e => setSort(e.target.value)}
                className="text-[9px] tracking-[0.12em] uppercase px-3 py-2 outline-none cursor-pointer flex-shrink-0"
                style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.bSub}`, color: C.text2, appearance: "auto" }}
                data-testid="cb-sort">
                <option value="votes">Top Voted</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>

            {/* Suggestion Cards */}
            {loading ? (
              <div className="text-center py-16"><Loader2 size={24} className="animate-spin mx-auto" style={{ color: C.gold }} /></div>
            ) : displayed.length === 0 ? (
              <div className="text-center py-16">
                <Palette size={36} className="mx-auto mb-3" style={{ color: `${C.gold}30` }} />
                <p className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>
                  {filter === "all" ? "No suggestions yet. Be the first!" : `No ${filter} suggestions found.`}
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="cb-suggestions-list">
                {displayed.map((s, i) => {
                  const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.pending;
                  const hasVoted = votedIds.has(s.id);
                  const isDenied = s.status === "denied";
                  return (
                    <div key={s.id}
                      className={`cb-card flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 ${isDenied ? "opacity-50" : ""}`}
                      style={{
                        background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent",
                        borderLeft: `3px solid ${cfg.color}`,
                      }}
                      data-testid={`cb-suggestion-${s.id}`}>
                      {/* Vote button */}
                      <button onClick={() => vote(s.id)} disabled={hasVoted || isDenied}
                        className="cb-vote-btn flex flex-col items-center gap-0.5 flex-shrink-0 transition-transform disabled:opacity-40 disabled:cursor-default"
                        data-testid={`cb-vote-${s.id}`}>
                        <ChevronUp size={18} style={{ color: hasVoted ? C.gold : C.text2 }} />
                        <span className="text-sm font-black leading-none" style={{ fontFamily: F.cond, color: hasVoted ? C.gold : C.text }}>{s.votes || 0}</span>
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium leading-snug ${isDenied ? "line-through" : ""}`}
                          style={{ fontFamily: F.sans, color: isDenied ? C.text2 : C.text }}>
                          {s.text}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-[8px] tracking-[0.15em] uppercase px-2 py-0.5 font-bold"
                            style={{ fontFamily: F.sans, background: cfg.bg, color: cfg.color }}>
                            {cfg.label}
                          </span>
                          <span className="text-[8px] tracking-wider uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>{s.category}</span>
                          <span className="text-[8px] tracking-wider" style={{ fontFamily: F.sans, color: C.text2 }}>
                            by {s.submitter_name}
                          </span>
                        </div>
                        {s.admin_note && (
                          <p className="mt-1.5 text-[10px] italic" style={{ fontFamily: F.sans, color: C.gold }}>
                            "{s.admin_note}" — Mickey
                          </p>
                        )}
                      </div>

                      {/* Rank badge for top 3 */}
                      {sort === "votes" && filter === "all" && i < 3 && !isDenied && (
                        <div className="hidden sm:flex items-center justify-center w-8 h-8 flex-shrink-0"
                          style={{ background: i === 0 ? C.gold : i === 1 ? "#94a3b8" : "#b45309", color: C.bg, fontFamily: F.cond, fontSize: "14px", fontWeight: 900 }}>
                          #{i + 1}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Back to collection CTA */}
            <div className="text-center mt-10">
              <Link to="/marker-collection"
                className="inline-flex items-center gap-3 px-7 py-3.5 text-[10px] sm:text-xs tracking-[0.25em] uppercase font-bold transition-all hover:gap-5 group"
                style={{ fontFamily: F.sans, border: `2px solid ${C.gold}30`, color: C.gold }}
                data-testid="cb-back-to-collection">
                VIEW THE COLLECTION <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
